import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    fields: any;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    amountHeadingAndText?: SmallHeadingAndTextStyles;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    erpOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    dataCell: {
        css: css`
            &&& {
                a span {
                    color: ${getColor("common.ui1")};
                    font-weight: bold;
                    &:hover,
                    &:focus {
                        color: ${getColor("primary.main")};
                    }
                }
            }
        `,
    },
};

const QuoteSummaryCard: FC<Props> = ({ quote, session, quoteSettings, fields, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }

    const {
        showQuoteNumber,
        showQuoteRefNumber,
        showQuoteStatus,
        showQuoteRequestedDate,
        showQuoteExpirationDate,
        showQuoteShipToAddress,
    } = fields;

    return (
        <>
            {showQuoteRequestedDate && (
                <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Requested")}>
                    {orderDateDisplay}
                </DataTableCell>
            )}
            {showQuoteNumber && (
                <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Quote Number")}>
                    <RfqQuoteDetailsPageTypeLink title={quote.orderNumber} quoteId={quote.id} />
                </DataTableCell>
            )}
            {showQuoteRefNumber && (
                <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Online Ref #")}>
                    {quote.erpOrderNumber ? (
                        <OrderDetailPageTypeLink title={quote.erpOrderNumber} orderNumber={quote.erpOrderNumber} />
                    ) : translate("N/A")}
                </DataTableCell>
            )}
            {showQuoteStatus && (
                <DataTableCell {...styles.dataCell} data-th={translate("Status")}>
                    {quote.statusDisplay}
                </DataTableCell>
            )}
            {showQuoteExpirationDate && (
                <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Expires")}>
                    {expirationDateDisplay}
                </DataTableCell>
            )}
            {showQuoteShipToAddress && (
                <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Ship To Address")}>
                    {quote.shipToFullAddress}
                </DataTableCell>
            )}
        </>
    );
};

export default QuoteSummaryCard;
